<template>
    <div>
        <p class="q-mb-sm"><strong>Select existing Te Kohinga Person</strong></p>
        <q-select
            :model-value="modelValue"
            :options="displayOptions"
            :loading="loading"
            :option-label="optionLabel"
            use-input
            clearable
            outlined
            dense
            input-debounce="300"
            :placeholder="modelValue ? '' : 'Search Te Kohinga People by name, alias, email or phone...'"
            @update:model-value="$emit('update:modelValue', $event)"
            @filter="onFilter"
        >
            <template v-slot:option="scope">
                <q-item v-bind="scope.itemProps">
                    <q-item-section v-if="isAnonymousTkOption(scope.opt)">
                        <q-item-label>{{ ANONYMOUS_LABEL }}</q-item-label>
                        <q-item-label caption>Log this form with no person recorded</q-item-label>
                    </q-item-section>
                    <q-item-section v-else>
                        <q-item-label>{{ optionLabel(scope.opt) }}</q-item-label>
                        <q-item-label caption>
                            {{ [scope.opt.email, scope.opt.phone].filter(Boolean).join(' · ') || 'No contact details' }}
                        </q-item-label>
                        <q-item-label v-if="scope.opt.aliases?.length" caption>
                            Also known as {{ aliasSummary(scope.opt) }}
                        </q-item-label>
                    </q-item-section>
                </q-item>
            </template>
            <template v-slot:no-option>
                <q-item>
                    <q-item-section class="text-grey-7">
                        No Te Kohinga People found. Use "Create new" below.
                    </q-item-section>
                </q-item>
            </template>
        </q-select>

        <slot name="actions">
            <div class="q-mt-sm q-gutter-sm row">
                <q-btn
                    flat dense no-caps size="md"
                    color="primary"
                    icon="o_person_add"
                    label="Create new Te Kohinga Person"
                    @click="showCreate = true"
                />

                <q-btn
                    flat dense no-caps size="md"
                    color="secondary"
                    icon="o_person_add_disabled"
                    label="Complete form anonymously"
                    @click="$emit('update:modelValue', ANONYMOUS_TK_OPTION)"
                />

                <div class="col"></div>

                <q-btn
                    v-if="isRealPersonSelected"
                    flat dense no-caps size="md"
                    color="primary"
                    icon="o_edit"
                    label="Edit details"
                    @click="showEdit = true"
                />
                <q-btn
                    v-if="isRealPersonSelected"
                    flat dense no-caps size="md"
                    color="secondary"
                    icon="o_link"
                    label="Assign to Paua Client"
                    @click="showLink = true"
                />
            </div>
        </slot>

        <TkPersonDialog
            v-model="showCreate"
            :origin-service="originService"
            @created="onCreated"
        />
        <TkPersonDialog
            v-model="showEdit"
            :person="modelValue"
            @updated="onUpdated"
        />
        <TkPersonLinkPauaDialog
            v-model="showLink"
            :person="modelValue"
            @linked="onLinked"
        />
    </div>
</template>

<script>
import api from "@/services/api/api";
import {
    tkPersonName,
    isAnonymousTkOption,
    ANONYMOUS_TK_OPTION,
    ANONYMOUS_LABEL,
} from "@/services/util/formSubject";
import TkPersonDialog from "./TkPersonDialog.vue";
import TkPersonLinkPauaDialog from "./TkPersonLinkPauaDialog.vue";

export default {
    name: "TkPersonSelect",
    components: { TkPersonDialog, TkPersonLinkPauaDialog },
    emits: ["update:modelValue", "linked-to-paua"],
    props: {
        modelValue: {
            type: [Object, null],
            default: null,
        },
        // Passed through to the create dialog so new records record their origin.
        originService: {
            type: String,
            default: "dynamic_forms",
        },
        // Hide anonymous option from selector
        allowAnonymous: {
            type: Boolean,
            default: true,
        },
    },
    data() {
        return {
            options: [],
            loading: false,
            initialLoaded: false,
            showCreate: false,
            showEdit: false,
            showLink: false,
            ANONYMOUS_LABEL,
            ANONYMOUS_TK_OPTION,
        };
    },
    computed: {
        displayOptions() {
            if (!this.allowAnonymous) return this.options;
            return [ANONYMOUS_TK_OPTION, ...this.options];
        },
        isRealPersonSelected() {
            return !!this.modelValue && !isAnonymousTkOption(this.modelValue);
        },
    },
    created() {
        this.loadPeople("");
    },
    methods: {
        isAnonymousTkOption,
        aliasSummary(person) {
            return person.aliases.map(alias => `${[alias.first_name, alias.last_name].filter(Boolean).join(" ")}${alias.source ? ` (${alias.source})` : ""}`).join(", ");
        },
        optionLabel(person) {
            if (isAnonymousTkOption(person)) return ANONYMOUS_LABEL;
            // tkPersonName already yields "Anonymous" for a nameless record.
            return tkPersonName(person);
        },
        async loadPeople(query) {
            this.loading = true;
            try {
                const res = await api.tkPeople.listPeople({ q: query || undefined, limit: 50 });
                this.options = res.data || [];
                this.initialLoaded = true;
            } catch (e) {
                this.options = [];
            } finally {
                this.loading = false;
            }
        },
        onFilter(val, update, abort) {
            if (!val && this.initialLoaded) {
                update();
                return;
            }
            api.tkPeople.listPeople({ q: val || undefined, limit: 50 })
                .then(res => {
                    update(() => { this.options = res.data || []; });
                })
                .catch(() => abort());
        },
        onCreated(person) {
            this.options = [person, ...this.options.filter(p => p.id !== person.id)];
            this.$emit("update:modelValue", person);
        },
        onUpdated(person) {
            this.options = this.options.map(p => p.id === person.id ? person : p);
            this.$emit("update:modelValue", person);
        },
        // Bubbles the link event up so the parent can switch context to the
        // newly-linked Paua client if it wants to.
        onLinked(payload) {
            this.$emit("linked-to-paua", payload);
        },
    },
};
</script>
